import type { BrandMergeGroup, ProjectDocument } from '@/types/database-schema';

// Two 5,000-alias sets, the 251 canonical labels, and the primary slug.
export const MAX_BRAND_QUERY_INPUT_ALIASES = 10252;
export const MAX_BRAND_QUERY_INPUT_BYTES = 2 * 1024 * 1024;
// Each source yields at most six spellings per expansion and nine query candidates.
// Grouped queries may also add one canonical label per group.
export const MAX_BRAND_QUERY_MATCHES = (MAX_BRAND_QUERY_INPUT_ALIASES + 251) * 6 * 6 * 9;
export const MAX_BRAND_QUERY_MATCH_BYTES = 64 * 1024 * 1024;

export class BrandQueryCapacityError extends Error {}

export function assertBrandQueryInputCapacity(value: unknown) {
  if (!Array.isArray(value)) return;
  if (value.length > MAX_BRAND_QUERY_INPUT_ALIASES) {
    throw new BrandQueryCapacityError('brand_variations supports at most 10,252 source aliases.');
  }
  const encoder = new TextEncoder();
  let bytes = 0;
  for (const alias of value) {
    if (typeof alias !== 'string') {
      throw new BrandQueryCapacityError('brand_variations must contain strings.');
    }
    // Count legacy overlong aliases in the raw budget; routes omit them before expansion.
    bytes += encoder.encode(alias).length + 1;
    if (bytes > MAX_BRAND_QUERY_INPUT_BYTES) {
      throw new BrandQueryCapacityError('brand_variations exceeds the 2 MiB source alias budget.');
    }
  }
}

const DIACRITICS_PATTERN = /[\u0300-\u036f]/g;
const SPACES_PATTERN = /\s+/g;
const MATCH_SEPARATOR_PATTERN = /[\s-]+/g;

function normalizeBrandBase(value: string): string {
  return value
    .normalize('NFKD')
    .replace(DIACRITICS_PATTERN, '')
    .toLowerCase()
    .trim();
}

function buildBrandSlugCandidate(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(DIACRITICS_PATTERN, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function normalizeBrandQueryBase(value: string): string {
  return value
    .normalize('NFC')
    .toLowerCase()
    .trim();
}

export const normalizeBrand = (value: string) =>
  normalizeBrandBase(value).replace(/[^a-z0-9]+/g, '');

export const normalizeBrandDisplayKey = (value: string) =>
  normalizeBrandBase(value).replace(SPACES_PATTERN, ' ');

function buildBrandMatchCandidates(value: string): string[] {
  const base = normalizeBrandBase(value);
  if (!base) {
    return [];
  }

  const spaceNormalized = base.replace(/-/g, ' ').replace(SPACES_PATTERN, ' ').trim();
  const hyphenNormalized = spaceNormalized.replace(SPACES_PATTERN, '-');
  const compact = normalizeBrand(base);
  const slugCandidate = buildBrandSlugCandidate(value);

  return Array.from(
    new Set(
      [base, spaceNormalized, hyphenNormalized, compact, slugCandidate].filter(
        (candidate): candidate is string => Boolean(candidate),
      ),
    ),
  );
}

function buildQueryBrandMatchCandidates(value: string): string[] {
  const normalized = new Set(buildBrandMatchCandidates(value));
  const base = normalizeBrandQueryBase(value);
  if (!base) {
    return Array.from(normalized);
  }

  const spaceNormalized = base.replace(/-/g, ' ').replace(SPACES_PATTERN, ' ').trim();
  const hyphenNormalized = spaceNormalized.replace(SPACES_PATTERN, '-');
  const compact = base.replace(MATCH_SEPARATOR_PATTERN, '');
  for (const candidate of [base, spaceNormalized, hyphenNormalized, compact]) {
    if (candidate) {
      normalized.add(candidate);
    }
  }

  return Array.from(normalized);
}

export function normalizeBrandVariationsForQuery(variations: string[]): string[] {
  const normalized = new Set<string>();
  for (const variation of variations) {
    for (const candidate of buildQueryBrandMatchCandidates(variation)) {
      normalized.add(candidate);
    }
  }
  return Array.from(normalized);
}

export interface ConfiguredBrandGroup {
  canonicalName: string;
  brandVariations: string[];
  matchSet: Set<string>;
  isProjectDefaultBrand: boolean;
  sourceVariations?: string[];
}

export interface ResolvedTrackedBrand {
  canonicalName: string;
  brandVariations: string[];
  isProjectDefaultBrand: boolean;
  isConfiguredBrand: boolean;
}

function addBrandVariationAliases(variations: Set<string>, raw: string) {
  const trimmed = raw.trim();
  if (!trimmed) {
    return;
  }

  variations.add(trimmed);
  for (const candidate of buildBrandMatchCandidates(trimmed)) {
    variations.add(candidate);
  }
}

export const buildExpandedBrandVariations = (values: string[]): string[] => {
  const variations = new Set<string>();
  values.forEach(value => addBrandVariationAliases(variations, value));
  return Array.from(variations);
};

export function expandBrandQueryVariations(
  values: string[],
  { maxItems = MAX_BRAND_QUERY_MATCHES, maxBytes = MAX_BRAND_QUERY_MATCH_BYTES } = {}
): string[] {
  const matches = new Set<string>();
  const encoder = new TextEncoder();
  let bytes = 0;
  for (const value of values) {
    // Reproduce the existing client expansion, including a merged default group.
    const spellings = buildExpandedBrandVariations(buildExpandedBrandVariations([value]));
    for (const match of normalizeBrandVariationsForQuery(spellings)) {
      if (matches.has(match)) continue;
      bytes += encoder.encode(match).length + 1;
      if (matches.size >= maxItems || bytes > maxBytes) {
        throw new BrandQueryCapacityError('Brand matching exceeds the derived alias budget.');
      }
      matches.add(match);
    }
  }
  return Array.from(matches);
}

const buildBrandVariationsFromParam = (raw: string): string[] => {
  return buildExpandedBrandVariations([raw]);
};

export const buildBrandMatchSet = (values: string[]): Set<string> => {
  const matchSet = new Set<string>();
  values.forEach(value => {
    for (const candidate of buildBrandMatchCandidates(value)) {
      matchSet.add(candidate);
      const compact = normalizeBrand(candidate);
      if (compact) {
        matchSet.add(compact);
      }
    }
  });
  return matchSet;
};

export const matchesBrandValue = (value: string, matchSet: Set<string>) => {
  const normalized = normalizeBrandBase(value);
  if (!normalized) {
    return false;
  }

  return matchSet.has(normalized) || matchSet.has(normalizeBrand(normalized));
};

const buildProjectBrandVariations = ({
  brandSlug,
  brandName,
  brandVariations,
}: {
  brandSlug: string;
  brandName?: string | null;
  brandVariations?: string[];
}) => {
  return buildExpandedBrandVariations([
    ...(brandVariations || []),
    brandSlug,
    brandName || '',
  ]);
};

export const buildConfiguredBrandGroups = ({
  brandName,
  projectBrandSlug,
  brandVariations,
  brandMergeGroups,
}: {
  brandName?: string | null;
  projectBrandSlug?: string | null;
  brandVariations?: string[];
  brandMergeGroups?: BrandMergeGroup[];
}): ConfiguredBrandGroup[] => {
  const groups: ConfiguredBrandGroup[] = [];

  if (brandName?.trim()) {
    const projectVariations = buildProjectBrandVariations({
      brandSlug: projectBrandSlug?.trim() || brandName,
      brandName,
      brandVariations,
    });
    groups.push({
      canonicalName: brandName.trim(),
      brandVariations: projectVariations,
      matchSet: buildBrandMatchSet(projectVariations),
      isProjectDefaultBrand: true,
      sourceVariations: [
        ...new Set(
          [...(brandVariations || []), projectBrandSlug?.trim() || brandName, brandName]
            .map(value => value.trim())
            .filter(Boolean)
        ),
      ],
    });
  }

  (brandMergeGroups || []).forEach(group => {
    const canonicalName = group?.canonical_name?.trim();
    if (!canonicalName) {
      return;
    }

    const variations = buildExpandedBrandVariations([
      canonicalName,
      ...(group.variations || []),
    ]);
    groups.push({
      canonicalName,
      brandVariations: variations,
      matchSet: buildBrandMatchSet(variations),
      isProjectDefaultBrand: false,
      sourceVariations: [
        ...new Set(
          [canonicalName, ...(group.variations || [])].map(value => value.trim()).filter(Boolean)
        ),
      ],
    });
  });

  const defaultGroup = groups.find(group => group.isProjectDefaultBrand);
  if (!defaultGroup) return groups;

  // A saved merge that includes the project brand defines the same identity.
  // Keep its canonical name and all aliases when entering through the default slug.
  const overlappingGroups = groups.filter(group =>
    !group.isProjectDefaultBrand &&
    group.brandVariations.some(variation => matchesBrandValue(variation, defaultGroup.matchSet))
  );
  if (overlappingGroups.length === 0) return groups;

  const mergedVariations = buildExpandedBrandVariations([
    ...defaultGroup.brandVariations,
    ...overlappingGroups.flatMap(group => group.brandVariations),
  ]);
  return [
    {
      canonicalName: overlappingGroups[0].canonicalName,
      brandVariations: mergedVariations,
      matchSet: buildBrandMatchSet(mergedVariations),
      isProjectDefaultBrand: true,
      sourceVariations: [
        ...new Set([
          ...(defaultGroup.sourceVariations || []),
          ...overlappingGroups.flatMap(group => group.sourceVariations || []),
        ]),
      ],
    },
    ...groups.filter(group => group !== defaultGroup && !overlappingGroups.includes(group)),
  ];
};

export function compactBrandQueryVariations(
  values: string[],
  project?: Partial<Pick<ProjectDocument, 'brand_settings' | 'brand_merge_groups'>> | null
) {
  try {
    assertBrandQueryInputCapacity(values);
    return values;
  } catch {
    // Oversized generated spellings can be replaced with their saved source aliases.
  }
  if (!project) return values;
  const groups = buildConfiguredBrandGroups({
    brandName: project.brand_settings?.brand_name,
    projectBrandSlug: project.brand_settings?.brand_slug,
    brandVariations: project.brand_settings?.brand_variations,
    brandMergeGroups: project.brand_merge_groups,
  });
  const key = (value: string) => value.trim().normalize('NFC').toLowerCase();
  const input = new Map(values.map(value => [key(value), value]));
  const result = new Map(input);
  for (const group of groups) {
    if (!group.sourceVariations || !group.brandVariations.every(value => input.has(key(value))))
      continue;
    group.brandVariations.forEach(value => result.delete(key(value)));
    group.sourceVariations.forEach(value => result.set(key(value), value));
  }
  return Array.from(result.values());
}

export const buildConfiguredBrandQueryGroups = (
  settings: Parameters<typeof buildConfiguredBrandGroups>[0],
): BrandMergeGroup[] => {
  // Send saved aliases, not generated spellings: the API bounds raw input before
  // expanding it for SQL matching, so expansion here could crowd out saved names.
  const rawAliases = Array.from(new Set([
    settings.brandName || '',
    settings.projectBrandSlug || '',
    ...(settings.brandVariations || []),
    ...(settings.brandMergeGroups || []).flatMap(group => [
      group.canonical_name,
      ...(group.variations || []),
    ]),
  ].map(value => value.trim()).filter(Boolean)));
  const configuredGroups = buildConfiguredBrandGroups(settings);
  const queryGroups: BrandMergeGroup[] = configuredGroups.map(group => ({
    canonical_name: group.canonicalName,
    variations: [],
  }));
  const groupsByMatchKey = new Map<string, number[]>();
  configuredGroups.forEach((group, index) => {
    group.matchSet.forEach(key => {
      const indexes = groupsByMatchKey.get(key) || [];
      indexes.push(index);
      groupsByMatchKey.set(key, indexes);
    });
  });
  for (const alias of rawAliases) {
    const normalized = normalizeBrandBase(alias);
    const indexes = new Set([
      ...(groupsByMatchKey.get(normalized) || []),
      ...(groupsByMatchKey.get(normalizeBrand(normalized)) || []),
    ]);
    indexes.forEach(index => queryGroups[index].variations.push(alias));
  }
  return queryGroups;
};

export const findConfiguredBrandGroup = (
  brand: string,
  groups: ConfiguredBrandGroup[],
) => {
  return groups.find(group => matchesBrandValue(brand, group.matchSet)) || null;
};

export const resolveTrackedBrand = ({
  brandSlug,
  brandName,
  projectBrandSlug,
  brandVariations,
  brandMergeGroups,
  configuredBrandGroups,
}: {
  brandSlug: string;
  brandName?: string | null;
  projectBrandSlug?: string | null;
  brandVariations?: string[];
  brandMergeGroups?: BrandMergeGroup[];
  configuredBrandGroups?: ConfiguredBrandGroup[];
}): ResolvedTrackedBrand => {
  const configuredGroups =
    configuredBrandGroups ??
    buildConfiguredBrandGroups({
      brandName,
      projectBrandSlug,
      brandVariations,
      brandMergeGroups,
    });
  const matchedGroup = findConfiguredBrandGroup(brandSlug, configuredGroups);

  if (!matchedGroup) {
    return {
      canonicalName: brandSlug,
      brandVariations: buildBrandVariationsFromParam(brandSlug),
      isProjectDefaultBrand: false,
      isConfiguredBrand: false,
    };
  }

  return {
    canonicalName: matchedGroup.canonicalName,
    brandVariations: matchedGroup.brandVariations,
    isProjectDefaultBrand: matchedGroup.isProjectDefaultBrand,
    isConfiguredBrand: true,
  };
};
